import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  DatePicker,
  Input,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import { formatAmount } from '../format';

const { Text } = Typography;

/**
 * The journal lines as they came from AB, with filters and the totals of
 * everything the filter matches.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @param {Object} props.filter - { period: dayjs|null, account, partner, orderNo, text }
 * @param {Function} props.onFilterChange - Called with the new filter
 * @param {number} props.year - Year of the delivery
 * @returns {JSX.Element} The lines table
 */
export default function AbLinesTab({
  companyId,
  filter,
  onFilterChange,
  year,
}) {
  const { t } = useTranslation('clientReports');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  // Text boxes apply on Enter / blur, not on every keystroke.
  const [draft, setDraft] = useState(filter);

  useEffect(() => {
    setDraft(filter);
    setPage(1);
  }, [filter]);

  const params = {
    year: filter.period ? filter.period.year() : year,
    month: filter.period ? filter.period.month() + 1 : undefined,
    account: filter.account || undefined,
    partner: filter.partner || undefined,
    orderNo: filter.orderNo || undefined,
    text: filter.text || undefined,
    page,
    pageSize,
  };

  const { data, isFetching, error } = useQuery({
    queryKey: ['client-reports-ab-lines', companyId, params],
    queryFn: () => API.clientReports.getAbLines(companyId, params),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const result = data?.data;

  const apply = () => {
    const changed = ['account', 'partner', 'orderNo', 'text'].some(
      (key) => (draft[key] || '') !== (filter[key] || '')
    );
    if (changed) onFilterChange({ ...draft });
  };
  const textInput = (key, placeholder, width) => (
    <Input
      allowClear
      style={{ width }}
      placeholder={placeholder}
      value={draft[key]}
      onChange={(e) => {
        const { value } = e.target;
        setDraft((d) => ({ ...d, [key]: value }));
        if (!value) onFilterChange({ ...draft, [key]: '' });
      }}
      onPressEnter={apply}
      onBlur={apply}
    />
  );

  const partnerCell = (code, name) =>
    code ? (
      <Tooltip title={name}>
        <Text>{code}</Text>
      </Tooltip>
    ) : null;

  const columns = [
    {
      title: t('ab.orderDate'),
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 100,
      render: (v) => dayjs(v).format('DD.MM.YYYY'),
    },
    { title: t('ab.order'), dataIndex: 'orderNo', key: 'orderNo', width: 100 },
    { title: t('ab.account'), dataIndex: 'account', key: 'account', width: 90 },
    {
      title: t('ab.analytic'),
      dataIndex: 'partner',
      key: 'partner',
      width: 110,
      render: (v, row) => partnerCell(v, row.partnerName),
    },
    {
      title: t('ab.counter'),
      dataIndex: 'counterCode',
      key: 'counterCode',
      width: 110,
      render: (v, row) => partnerCell(v, row.counterName),
    },
    {
      title: t('ab.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: t('ab.invoice'),
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      width: 140,
      ellipsis: true,
    },
    {
      title: t('ab.debit'),
      dataIndex: 'debit',
      key: 'debit',
      width: 140,
      align: 'right',
      render: (v) => (v ? formatAmount(v) : ''),
    },
    {
      title: t('ab.credit'),
      dataIndex: 'credit',
      key: 'credit',
      width: 140,
      align: 'right',
      render: (v) => (v ? formatAmount(v) : ''),
    },
    {
      title: t('ab.fx'),
      key: 'fx',
      width: 120,
      align: 'right',
      render: (_, row) =>
        row.currency
          ? `${formatAmount(row.debitFx || row.creditFx)} ${row.currency}`
          : '',
    },
  ];

  const errorMessage =
    error?.response?.status === 404
      ? t('fccs.noLedger', { year: params.year })
      : error?.response?.data?.message || error?.message;

  return (
    <div>
      <Space wrap style={{ display: 'flex', marginBottom: 12 }}>
        <DatePicker
          picker="month"
          format="MM.YYYY"
          placeholder={t('ab.wholeYear')}
          value={filter.period}
          onChange={(value) =>
            onFilterChange({
              ...draft,
              period: value ? value.startOf('month') : null,
            })
          }
        />
        {textInput('account', t('ab.accountPrefix'), 140)}
        {textInput('partner', t('ab.partnerCode'), 140)}
        {textInput('orderNo', t('ab.order'), 120)}
        {textInput('text', t('ab.descriptionSearch'), 220)}
        <Button shape="round" onClick={apply}>
          {t('ab.filter')}
        </Button>
      </Space>

      {errorMessage && (
        <Alert
          type="warning"
          showIcon
          message={errorMessage}
          style={{ marginBottom: 12 }}
        />
      )}

      {result && (
        <Space size="large" style={{ display: 'flex', marginBottom: 8 }}>
          <Text>{t('ab.found', { count: result.total })}</Text>
          <Text>
            {t('ab.debit')}: <Text strong>{formatAmount(result.debit)}</Text>
          </Text>
          <Text>
            {t('ab.credit')}: <Text strong>{formatAmount(result.credit)}</Text>
          </Text>
          <Text>
            {t('ab.balance')}:{' '}
            <Text strong>{formatAmount(result.debit - result.credit)}</Text>
          </Text>
        </Space>
      )}

      <Table
        rowKey="_id"
        size="small"
        loading={isFetching}
        columns={columns}
        dataSource={result?.items || []}
        scroll={{ x: 1300 }}
        pagination={{
          current: page,
          pageSize,
          total: result?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: [50, 100, 200, 500],
          onChange: (p, s) => {
            setPage(s !== pageSize ? 1 : p);
            setPageSize(s);
          },
        }}
      />
    </div>
  );
}
